/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useState } from 'react';
import { SEO, ShieldCheckIcon, ChevronDownIcon } from '../components/common';
import { colors, borderRadius, shadows, typography, transitions } from '../styles/theme';
import { gizlilikPolitikasi } from '../data/gizlilikPolitikasi';
import { aydinlatmaMetni } from '../data/aydinlatmaMetni';
import { mesafeliSozlesme } from '../data/mesafeliSozlesme';
import { kvkkSozlesmesi } from '../data/kvkkSozlesmesi';

const pageStyles = css`
  background: ${colors.background.secondary};
`;

const heroStyles = css`
  background: linear-gradient(135deg, ${colors.primary[600]} 0%, ${colors.primary[800]} 100%);
  color: white;
  padding: 3rem 1rem;
  text-align: center;

  @media (min-width: 768px) {
    padding: 4rem 2rem;
  }
`;

const heroContentStyles = css`
  max-width: 800px;
  margin: 0 auto;

  .icon {
    width: 64px;
    height: 64px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: ${borderRadius.full};
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.25rem;

    svg {
      width: 30px;
      height: 30px;
    }
  }

  h1 {
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize['3xl']};
    font-weight: ${typography.fontWeight.extrabold};
    margin-bottom: 0.75rem;

    @media (min-width: 768px) {
      font-size: ${typography.fontSize['4xl']};
    }
  }

  p {
    font-size: ${typography.fontSize.base};
    color: rgba(255, 255, 255, 0.85);
    margin: 0;
  }
`;

const containerStyles = css`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem 4rem;

  @media (min-width: 768px) {
    padding: 3rem 2rem 5rem;
  }
`;

const accordionStyles = css`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const accordionItemStyles = css`
  background: white;
  border-radius: ${borderRadius.xl};
  box-shadow: ${shadows.sm};
  overflow: hidden;
  transition: box-shadow ${transitions.fast};

  &:hover {
    box-shadow: ${shadows.md};
  }
`;

const accordionHeaderStyles = css`
  margin: 0;

  button {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.25rem 1.5rem;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize.lg};
    font-weight: ${typography.fontWeight.bold};
    color: ${colors.primary[700]};
    transition: background ${transitions.fast};

    &:hover {
      background: ${colors.neutral[50]};
    }
  }

  .chevron {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    color: ${colors.neutral[400]};
    transition: transform ${transitions.fast};
  }

  .chevron.open {
    transform: rotate(180deg);
  }
`;

const accordionContentStyles = css`
  padding: 0 1.5rem 1.5rem;
  color: ${colors.text.secondary};
  font-size: ${typography.fontSize.sm};
  line-height: 1.8;

  h3 {
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize.base};
    font-weight: ${typography.fontWeight.semibold};
    color: ${colors.text.primary};
    margin: 1.25rem 0 0.5rem;
  }

  p {
    margin-bottom: 0.75rem;
  }

  ul {
    padding-left: 1.25rem;
    margin-bottom: 0.75rem;

    li {
      list-style: disc;
      margin-bottom: 0.35rem;
    }
  }
`;

const footerNoteStyles = css`
  text-align: center;
  color: ${colors.text.muted};
  font-size: ${typography.fontSize.sm};
  margin-top: 2rem;
`;

interface LegalSection {
  heading: string;
  paragraphs?: string[];
  list?: string[];
}

interface LegalDocument {
  title: string;
  sections: LegalSection[];
}

const legalDocuments: LegalDocument[] = [
  gizlilikPolitikasi,
  aydinlatmaMetni,
  mesafeliSozlesme,
  kvkkSozlesmesi,
];

function renderSections(sections: LegalSection[]) {
  return sections.map((section, i) => (
    <div key={i}>
      <h3>{section.heading}</h3>
      {section.paragraphs?.map((p, j) => <p key={j}>{p}</p>)}
      {section.list && (
        <ul>
          {section.list.map((item, k) => <li key={k}>{item}</li>)}
        </ul>
      )}
    </div>
  ));
}

export default function SozlesmelerPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div css={pageStyles}>
      <SEO
        title="Yasal Metinler - Gizlilik Politikası & Sözleşmeler"
        description="YolSepetiGO gizlilik politikası, aydınlatma metni, mesafeli kullanıcı sözleşmesi ve KVKK sözleşmesi."
        keywords={['gizlilik politikası', 'kvkk', 'sözleşme', 'aydınlatma metni', 'yasal metinler']}
      />

      {/* Hero */}
      <section css={heroStyles}>
        <div css={heroContentStyles}>
          <div className="icon"><ShieldCheckIcon /></div>
          <h1>Yasal Metinler</h1>
          <p>Yol SepetiGO Teknoloji</p>
        </div>
      </section>

      {/* Accordion */}
      <div css={containerStyles}>
        <div css={accordionStyles}>
          {legalDocuments.map((doc, index) => (
            <div key={index} css={accordionItemStyles}>
              <h2 css={accordionHeaderStyles}>
                <button
                  onClick={() => toggleAccordion(index)}
                  aria-expanded={openIndex === index}
                  aria-controls={`legal-doc-${index}`}
                >
                  {doc.title}
                  <ChevronDownIcon className={`chevron ${openIndex === index ? 'open' : ''}`} />
                </button>
              </h2>
              {openIndex === index && (
                <div css={accordionContentStyles} id={`legal-doc-${index}`}>
                  {renderSections(doc.sections)}
                </div>
              )}
            </div>
          ))}
        </div>

        <p css={footerNoteStyles}>
          Yol SepetiGO Teknoloji - www.yolsepetigo.com
        </p>
      </div>
    </div>
  );
}
